import { NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { Storage } from '@targx/libs/storage';
import { WarehouseService } from '@core/services/warehouse.service';
@Component({
  selector: 'compact-layout',
  templateUrl: './compact.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  styles: [
    `
      fuse-vertical-navigation{
        --fuse-horizontal-navigation-height: 64px;
        --fuse-vertical-navigation-height: calc(100vh - var(--fuse-horizontal-navigation-height));
        min-height: var(--fuse-vertical-navigation-height) !important;
        max-height: var(--fuse-vertical-navigation-height) !important;
        height: var(--fuse-vertical-navigation-height) !important;
        top: var(--fuse-horizontal-navigation-height) !important;
      }
      .tx-header{
            box-shadow: -1px 13px 19px -17px #000;
            z-index: 49;
      }
      .header-logo{
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        background-color: #fff;
        box-shadow: inset 0 -2px 5px #fff, inset 0 2px 5px #fff, inset 7px 0 14px -13px rgba(0, 0, 0, .75), inset -7px 0 14px -13px rgba(0, 0, 0, .75);
        position: relative;
        -webkit-transform: skew(45deg);
        .nav-brand{
          -webkit-transform: skew(-45deg);
          -moz-transform: skew(-45deg);
          -o-transform: skew(-45deg);
          margin-right: 0;
          padding: 0;
          text-align: center;
          img{
            max-height: 30px;
            padding: 0 20px;
          }
        }
      }
    `
  ],
  imports: [FuseLoadingBarComponent,
    MatButtonModule, MatIconModule, LanguagesComponent,
    FuseFullscreenComponent, SearchComponent, ShortcutsComponent,
    MessagesComponent, NotificationsComponent, UserComponent, NgIf,
    RouterOutlet, QuickChatComponent, FuseVerticalNavigationComponent, MatMenuModule],
})
export class CompactLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigation: Navigation;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  selectedOption = signal('Farmácia');
  private readonly WarehouseService = inject(WarehouseService);

  warehouses = signal([]);


  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
  ) {
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  setWarehouse(warehouseId: string): void {
    Storage.set(
      environment.KEY_WAREHOUSE_SELECTED,
      warehouseId
    );
    this.selectedOption.set(this.warehouses().find(w => w.id === warehouseId)?.name || 'Farmácia');
    window.location.reload();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

    this.WarehouseService.findMany()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((warehouses) => {
        this.warehouses.set(warehouses);
        this.selectedOption.set(warehouses.find(w => w.id === Storage.get(environment.KEY_WAREHOUSE_SELECTED))?.name || 'Farmácia');
      });

  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
}
